import { TranslateService } from "@ngx-translate/core";
import { hasValue } from "src/app/angular-core/utils/functions/common.functions";
import { getTranslationKeyForValue } from "src/app/angular-core/utils/functions/translation.functions";

export class EnumTranslationMap<T extends string> {
  private stringMap: Map<T, string>;
  private enumMap: Map<string, T>;

  constructor(
    private translationPrefix: string,
    private translate: TranslateService,
    private enumObj: object
  ) {
  }

  private initialize(): void {
    if (this.stringMap && this.enumMap) {
      return;
    }

    if (!this.translationPrefix.endsWith('.')) {
      this.translationPrefix += '.';
    }

    this.stringMap = new Map();
    this.enumMap = new Map();
    Object.values(this.enumObj).forEach(objValue => {
      const enumValue = objValue as T;
      const key = getTranslationKeyForValue(enumValue);
      const stringValue = this.translate.instant(this.translationPrefix + key);
      this.stringMap.set(enumValue, stringValue);
      this.enumMap.set(stringValue, enumValue);
    });
  }

  public getTranslation(value: T): string {
    this.initialize();

    return this.stringMap.get(value);
  }

  public getTranslations(values: T[]): string[] {
    this.initialize();

    return values.map(v => this.stringMap.get(v));
  }

  public getEnum(value: string): T {
    this.initialize();

    return this.enumMap.get(value);
  }

  public getEnums(values: string[]): T[] {
    this.initialize();

    return values.map(v => this.enumMap.get(v));
  }

  public getCommaSeparatedString(values: T[]): string {
    this.initialize();

    return values.map(v => this.stringMap.get(v)).join(', ');
  }

  public getCommaSeparatedStringOrNone(values: T[]): string {
    this.initialize();

    if (values.length > 0) {
      return values.map(v => this.stringMap.get(v)).join(', ');
    }

    return this.translate.instant(this.translationPrefix + 'None');
  }

  public parseCommaSeperatedString(string: string): T[] {
    this.initialize();

    const parts = string.split(',');
    const result: T[] = [];
    for (const part of parts) {
      const enumValue = this.enumMap.get(part.trim());
      if (hasValue(enumValue)) {
        result.push(enumValue);
      }
    }
    return result;
  }

  public getAllTranslations(): string[] {
    this.initialize();

    return [...this.enumMap.keys()];
  }

  public getAllEnumValues(): T[] {
    this.initialize();

    return [...this.stringMap.keys()];
  }
}
